import { postRequests, getRequest } from '@/service/requests';
import NewRelicService from '@/service/new-relic-service';

export default class ShopifyAPI {
  static CartSettings;

  static setCartCreatedTime() {
    // Set cart timestamp when adding first item
    if (!localStorage.getItem('cartCreatedAt')) {
      return;
    }

    // Check cart age on page load
    const cartCreatedAt = new Date(localStorage.getItem('cartCreatedAt'));
    const now = new Date();
    const twoWeeksMs = 14 * 24 * 60 * 60 * 1000;

    if (now - cartCreatedAt > twoWeeksMs) {
      fetch('/cart/clear.js', { method: 'POST' }).then(() =>
        localStorage.removeItem('cartCreatedAt')
      );
    }
  }
  /**
   *
   * @param {Object} formData
   * @param {Array} formData.items
   * @returns {Promise<Response|{error}>}
   */
  static async addToCart(formData) {
    if (!localStorage.getItem('cartCreatedAt')) {
      localStorage.setItem('cartCreatedAt', new Date().toISOString());
    }

    const settings = ShopifyAPI.getCartSettings();
    let formToSend = formData;

    if (settings?.freeSampleProduct) {
      formToSend = ShopifyAPI.getFormDataWIthFreeSample({
        formData,
        freeVariantId: settings.freeSampleProduct,
        loyaltyLevel: settings?.customerLoyalty
      });
    }

    const url = window.Shopify.routes.root + 'cart/add.js';
    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formToSend)
    };

    NewRelicService.recordAddToCart(formToSend);
    try {
      const response = await fetch(url, config);
      return response;
    } catch (e) {
      return { error: e };
    }
  }

  /**
   *
   * @returns {Promise<boolean>}
   */
  static async clearCart() {
    const url = window.Shopify.routes.root + 'cart/clear.js';
    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };
    try {
      await postRequests(url, {}, config);
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  /**
   *
   * @param {Object} items
   * @returns {Promise<boolean>}
   */
  static async updateCart(items) {
    try {
      const url = window.Shopify.routes.root + 'cart/update.js';
      const config = {
        headers: {
          'Content-Type': 'application/json'
        }
      };
      const body = { updates: items };
      await postRequests(url, body, config);
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  /**
   *
   * @returns {Promise<{error}|{items}>}
   */
  static async getCart() {
    try {
      const url = window.Shopify.routes.root + 'cart.js';
      const response = await getRequest(url);
      return response.data;
    } catch (e) {
      console.error(e);
      return { error: e };
    }
  }

  static async getItemByHandle(handle) {
    try {
      const response = await getRequest(
        `${window.Shopify.routes.root}products/${handle}.js`
      );
      return response.data;
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  /**
   *
   * @param {Object} items
   * @returns {Promise<boolean>}
   */
  static async changeCart(formData) {
    const url = window.Shopify.routes.root + 'cart/change.js';
    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    };
    try {
      await fetch(url, config);
    } catch (error) {
      console.log('error =>', error);
    }
  }

  /**
   * Replaces an existing cart item with a new variant.
   *
   * @param {string} lineItemKey - The key of the existing line item to remove.
   * @param {string|number} newVariantId - The ID of the new variant to add.
   * @param {Object} payload - Additional item properties.
   * @param {number} [quantity=1] - The quantity of the new variant to add.
   * @returns {Promise<void>} - A promise that resolves when the replacement is complete.
   */
  static async replaceItemWithNewVariant(
    lineItemKey,
    newVariantId,
    payload,
    quantity = 1
  ) {
    await ShopifyAPI.changeCart({
      id: `${lineItemKey}`,
      quantity: 0
    });
    await ShopifyAPI.getCart();
    await ShopifyAPI.addToCart({
      items: [
        {
          ...payload,
          id: newVariantId,
          quantity
        }
      ]
    });
  }

  static getCartSettings() {
    if (ShopifyAPI.CartSettings) return ShopifyAPI.CartSettings;

    try {
      const settings = JSON.parse(window.localStorage.getItem('fob-settings'));
      ShopifyAPI.CartSettings = {
        freeSampleProduct: settings.free_sample_product,
        customerLoyalty: settings.customer_loyalty
      };
      return ShopifyAPI.CartSettings;
    } catch (e) {
      ShopifyAPI.CartSettings = null;
    }

    return ShopifyAPI.CartSettings;
  }

  static getFormDataWIthFreeSample({ freeVariantId, loyaltyLevel, formData }) {
    const newFormData = formData;

    const loyaltyHasGroupWord =
      loyaltyLevel === 'Group_A' || loyaltyLevel === 'Group_C';

    if (!loyaltyHasGroupWord) return newFormData;

    if (formData.items.includes((x) => x.id === freeVariantId))
      return newFormData;

    const freeItem = {
      id: freeVariantId,
      quantity: 1,
      properties: {
        _free_sample_product: true
      }
    };

    newFormData.items.push(freeItem);

    return newFormData;
  }

  /**
   * Updates the content of a Shopify section by ID
   *
   * @param {string} sectionId - The ID of the section to update (without the "shopify-section-" prefix)
   * @returns {promise}
   */
  static updateSection(sectionId) {
    return fetch(`/?section_id=${sectionId}`)
      .then((response) => response.text())
      .then((html) => {
        const parser = new DOMParser();
        const newDocument = parser.parseFromString(html, 'text/html');

        const newSection = newDocument.querySelector(
          `#shopify-section-${sectionId}`
        );

        const currentSection = document.querySelector(
          `#shopify-section-${sectionId}`
        );
        if (currentSection && newSection) {
          currentSection.innerHTML = newSection.innerHTML;
        }
      })
      .catch((error) => {
        console.error('Error on update section:', error);
      });
  }
}
